import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Empty, Loader, Pill } from "@/components/Bits";

const tone = { completed: "green", in_progress: "amber", not_started: "slate" };
const paceTone = { ahead: "green", on_track: "blue", behind: "amber" };

export default function Progress() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/progress").then(({ data }) => setData(data));
  }, []);

  if (!data) return <Shell><Loader label="Loading progress…" /></Shell>;
  if (!data.has_curriculum) return <Shell><Empty title="No approved curriculum yet" note="Progress appears once your guardian approves a plan." /></Shell>;

  return (
    <Shell>
      <div className="reveal" data-testid="progress-root">
        <p className="rule-label">
          {data.curriculum.label} · v{data.curriculum.version} · no leaderboards, no rankings
        </p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">Progress</h1>

        {data.pace?.status && (
          <div className="mt-6 card-flat p-6" data-testid="progress-pace">
            <Pill tone={paceTone[data.pace.status] || "slate"}>{data.pace.status.replace("_", " ")}</Pill>
            <p className="mt-3 text-sm text-slate-700">{data.pace.message}</p>
          </div>
        )}

        <div className="mt-6 grid sm:grid-cols-4 gap-4">
          {[
            ["Topics", `${data.topics.completed}/${data.topics.total}`],
            ["Chapters", `${data.chapters.completed}/${data.chapters.total}`],
            ["Subjects", `${data.subjects.completed}/${data.subjects.total}`],
            ["Completion", `${data.topics.percent}%`],
          ].map(([k, v]) => (
            <div key={k} className="card-flat p-6">
              <p className="rule-label">{k}</p>
              <p className="mt-3 font-display text-3xl font-extrabold">{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-4">
          {data.tree.map((subject) => (
            <div key={subject.subject_id} className="card-flat p-6" data-testid={`progress-subject-${subject.subject_id}`}>
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-display text-xl font-semibold">{subject.name}</h3>
                <span className="text-sm text-slate-500">
                  {subject.progress.completed}/{subject.progress.total} · {subject.progress.percent}%
                </span>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-teal-600 transition-all duration-500" style={{ width: `${subject.progress.percent}%` }} />
              </div>
              <div className="mt-5 space-y-4">
                {subject.chapters.map((chapter) => (
                  <div key={chapter.chapter_id}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold">{chapter.name}</p>
                      <span className="text-xs text-slate-500">
                        {chapter.progress.completed}/{chapter.progress.total}
                      </span>
                    </div>
                    <ul className="mt-2 divide-y divide-slate-100">
                      {chapter.topics.map((topic) => (
                        <li key={topic.topic_id} className="py-2.5 flex items-center justify-between gap-3">
                          <span className="text-sm text-slate-600">{topic.title}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            {topic.best_score > 0 && <span className="text-xs text-slate-500">{topic.best_score}%</span>}
                            <Pill tone={tone[topic.status]}>{topic.status.replace("_", " ")}</Pill>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
